import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Package, LogOut, MapPin, Clock, CheckCircle2, AlertCircle,
  Truck, ChevronRight, Plus, RefreshCw, Loader2, User, RotateCcw, Lock, Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import PasswordChangeDialog from "@/components/PasswordChangeDialog";
import MobileCustomerHome from "@/mobile/MobileCustomerHome";
import { shouldUseMobileShell } from "@/mobile/MobileShellProvider";

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem("customer_token");

const STATUS_STYLES = {
  pending: { label: "Awaiting payment", cls: "bg-amber-100 text-amber-700", icon: Clock },
  awaiting_driver: { label: "Finding a driver", cls: "bg-blue-100 text-blue-700", icon: Clock },
  accepted: { label: "Driver accepted", cls: "bg-indigo-100 text-indigo-700", icon: Truck },
  in_progress: { label: "In progress", cls: "bg-orange-100 text-orange-700", icon: Truck },
  completed: { label: "Completed", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-700", icon: AlertCircle },
  other: { label: "Processing", cls: "bg-slate-100 text-slate-600", icon: Clock },
};

function StatusBadge({ group }) {
  const s = STATUS_STYLES[group] || STATUS_STYLES.other;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${s.cls}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function BookingCard({ b, onRebook, rebookLoading }) {
  // Allow "Book Again" on any booking — the customer just wants to repeat the route.
  const canRebook = true;
  return (
    <div className="bg-white rounded-xl border border-slate-200 hover:shadow-md transition-all overflow-hidden" data-testid={`booking-card-${b.id}`}>
      <Link
        to={`/customer/booking/${b.id}`}
        className="block p-4 active:scale-[0.99]"
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-[10px] font-mono text-slate-400">#{b.id.slice(0, 8).toUpperCase()}</p>
            <StatusBadge group={b.status_group} />
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-slate-900">&pound;{(b.total_cost || 0).toFixed(2)}</p>
            <p className="text-[10px] text-slate-400">{b.distance_miles ? `${b.distance_miles} mi` : ""}</p>
          </div>
        </div>

        <div className="space-y-1.5 mt-3 text-sm">
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
            <span className="text-slate-700 truncate">{b.pickup_address || b.pickup_postcode}</span>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
            <span className="text-slate-700 truncate">{b.delivery_address || b.delivery_postcode}</span>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 text-xs text-slate-400">
          <span className="capitalize">{b.item_type} · {b.size}</span>
          <ChevronRight className="w-4 h-4" />
        </div>
      </Link>
      {canRebook && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRebook(b); }}
          disabled={rebookLoading}
          className="w-full py-2 border-t border-slate-100 bg-slate-50 hover:bg-orange-50 text-xs font-semibold text-orange-600 flex items-center justify-center gap-1.5 active:scale-[0.99] transition-colors disabled:opacity-60"
          data-testid={`rebook-btn-${b.id}`}
        >
          {rebookLoading === b.id ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <>
              <RotateCcw className="w-3.5 h-3.5" /> Book this again
            </>
          )}
        </button>
      )}
    </div>
  );
}

export default function CustomerDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rebookLoading, setRebookLoading] = useState(null);

  const load = useCallback(async () => {
    if (!token()) { navigate("/customer/login"); return; }
    setRefreshing(true);
    try {
      const [meRes, bRes] = await Promise.all([
        axios.get(`${API}/api/customer/me`, { headers: { Authorization: `Bearer ${token()}` }, validateStatus: () => true }),
        axios.get(`${API}/api/customer/bookings`, { headers: { Authorization: `Bearer ${token()}` }, validateStatus: () => true }),
      ]);
      if (meRes.status === 401) { localStorage.removeItem("customer_token"); navigate("/customer/login"); return; }
      setProfile(meRes.data);
      setBookings(bRes.data?.bookings || []);
    } catch (e) {
      toast.error("Could not load your dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 20000); // soft-refresh every 20s
    return () => clearInterval(interval);
  }, [load]);

  const logout = () => {
    localStorage.removeItem("customer_token");
    localStorage.removeItem("customer_profile");
    navigate("/customer/login");
  };

  const [deleting, setDeleting] = useState(false);
  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      // Use axios (not fetch) — under CapacitorHttp on iOS, fetch responses
      // have their body stream pre-consumed and calling .json() throws
      // "Body is disturbed or locked". axios uses XHR internally which
      // CapacitorHttp handles correctly. Required for App Store 5.1.1(v)
      // compliance so the delete flow works inside the .ipa too.
      await axios.delete(`${API}/api/customer/me`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      localStorage.removeItem("customer_token");
      localStorage.removeItem("customer_profile");
      toast.success("Account deleted", {
        description: "Your account and personal data have been permanently removed.",
      });
      // Land on the homepage so the (now-unauthenticated) user can sign up
      // again or leave the site entirely.
      navigate("/");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Please try again or contact support.";
      toast.error("Could not delete account", { description: detail });
    } finally {
      setDeleting(false);
    }
  };

  const handleRebook = async (b) => {
    setRebookLoading(b.id);
    try {
      const res = await axios.post(`${API}/api/customer/bookings/${b.id}/rebook`, null, {
        headers: { Authorization: `Bearer ${token()}` },
        validateStatus: () => true,
      });
      const data = res.data;
      if (res.status < 200 || res.status >= 300) { toast.error(data?.detail || "Could not rebook"); return; }
      sessionStorage.setItem(`rebook_prefill_${data.quote_id}`, JSON.stringify(data.prefill));
      toast.success("Route copied — complete booking to confirm");
      navigate(`/booking/${data.quote_id}`);
    } catch {
      toast.error("Network error");
    } finally {
      setRebookLoading(null);
    }
  };

  const filtered = bookings.filter(b => {
    if (filter === "all") return true;
    if (filter === "active") return ["awaiting_driver", "accepted", "in_progress"].includes(b.status_group);
    if (filter === "completed") return b.status_group === "completed";
    if (filter === "cancelled") return b.status_group === "cancelled";
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const counts = {
    all: bookings.length,
    active: bookings.filter(b => ["awaiting_driver", "accepted", "in_progress"].includes(b.status_group)).length,
    completed: bookings.filter(b => b.status_group === "completed").length,
    cancelled: bookings.filter(b => b.status_group === "cancelled").length,
  };

  // Mobile shell — render premium home unless user navigated to a filter view.
  const mobileShell = shouldUseMobileShell();
  const queryFilter = new URLSearchParams(location.search).get("filter");
  if (mobileShell && !queryFilter) {
    return (
      <div className="min-h-screen" data-testid="customer-dashboard">
        <MobileCustomerHome profile={profile} bookings={bookings} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-10" data-testid="customer-dashboard">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-3xl mx-auto px-4 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
              <User className="w-5 h-5 text-orange-300" />
            </div>
            <div>
              <p className="text-xs text-slate-400">Welcome back,</p>
              <p className="font-semibold" data-testid="customer-name">{profile?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2 text-slate-400 hover:text-white">
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <PasswordChangeDialog
              endpoint="/api/customer/me/password"
              tokenKey="customer_token"
              testId="customer-change-password-btn"
              trigger={
                <button className="p-2 text-slate-400 hover:text-white cursor-pointer" title="Change password" data-testid="customer-change-password-trigger">
                  <Lock className="w-4 h-4" />
                </button>
              }
            />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  className="p-2 text-slate-400 hover:text-red-400 cursor-pointer transition-colors"
                  title="Delete account"
                  data-testid="customer-delete-account-trigger"
                  aria-label="Delete account"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="customer-delete-account-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete your Transport Anything 247
                    account and remove your personal data from our systems.
                    Booking history required for accounting will be kept but
                    anonymised. This action <strong>cannot be undone</strong>.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel
                    disabled={deleting}
                    data-testid="customer-delete-account-cancel"
                  >
                    Keep account
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                    data-testid="customer-delete-account-confirm"
                    className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                  >
                    {deleting ? "Deleting…" : "Delete permanently"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button variant="ghost" size="sm" onClick={logout} className="text-slate-300 hover:text-white hover:bg-white/10" data-testid="customer-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 -mt-4">
        {/* New booking CTA */}
        <Link
          to="/"
          className="block bg-orange-500 hover:bg-orange-600 text-white rounded-xl px-4 py-3 shadow-lg mb-4 flex items-center justify-between active:scale-[0.98] transition-all"
          data-testid="new-booking-btn"
        >
          <div>
            <p className="font-semibold">Need to send something?</p>
            <p className="text-xs text-orange-100">Get an instant quote in 30 seconds</p>
          </div>
          <Plus className="w-6 h-6" />
        </Link>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1">
          {[
            { k: "all", label: "All" },
            { k: "active", label: "Active" },
            { k: "completed", label: "Completed" },
            { k: "cancelled", label: "Cancelled" },
          ].map(f => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border ${
                filter === f.k
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200"
              }`}
              data-testid={`filter-${f.k}`}
            >
              {f.label} <span className="ml-1 opacity-70">{counts[f.k]}</span>
            </button>
          ))}
        </div>

        {/* Bookings list */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="font-semibold text-slate-700">No bookings here yet</p>
            <p className="text-sm text-slate-500 mt-1">Your bookings will appear here.</p>
            <Link to="/" className="inline-block mt-3 text-sm text-orange-600 font-medium">Get a quote &rarr;</Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="bookings-list">
            {filtered.map(b => <BookingCard key={b.id} b={b} onRebook={handleRebook} rebookLoading={rebookLoading} />)}
          </div>
        )}
      </div>
    </div>
  );
}
